import React, { useEffect, useRef, useState } from 'react'
import { ThemeToggle } from '../../components/ui'
import { useUser, useSignedInUser, User } from '../../contexts/UserContext'
import { apiClient } from '../../lib/api'
import DocumentTemplateManager from '../../components/document-templates/DocumentTemplateManager'

type Tab = 'profile' | 'document_templates'

const UserSettings: React.FC = () => {
  const user = useSignedInUser()
  const [tab, setTab] = useState<Tab>('profile')

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          User Settings
        </h1>
        <p className="text-muted-foreground mb-6">
          Manage your personal account settings and preferences
        </p>

        <div className="border-b border-border mb-6 flex items-center gap-1">
          <TabButton active={tab === 'profile'} onClick={() => setTab('profile')}>Profile</TabButton>
          <TabButton active={tab === 'document_templates'} onClick={() => setTab('document_templates')}>
            Document Templates
          </TabButton>
        </div>

        {tab === 'profile' && <ProfileTab user={user} />}
        {tab === 'document_templates' && (
          <DocumentTemplateManager
            scope={{ level: 'program_manager', ownerId: user.id }}
            subtitle="Your personal customizations, applied across every program you own. Override any system or office template here."
          />
        )}
      </div>
    </div>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

function ProfileTab({ user }: { user: User }) {
  const { refetch } = useUser()
  const initial = {
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
    username: user.username || '',
    phone: user.phone || '',
  }
  const [form, setForm] = useState(initial)
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const savedResetTimerRef = useRef<number | null>(null)

  const isDirty =
    form.first_name !== initial.first_name ||
    form.last_name !== initial.last_name ||
    form.email !== initial.email ||
    form.username !== initial.username ||
    form.phone !== initial.phone

  const handleSave = async () => {
    setStatus('saving')
    setErrorMsg(null)
    try {
      await apiClient.patch('/api/users/me/', form)
      setStatus('saved')
      await refetch()
      if (savedResetTimerRef.current) clearTimeout(savedResetTimerRef.current)
      savedResetTimerRef.current = window.setTimeout(() => setStatus('idle'), 2000)
    } catch (err: any) {
      setStatus('error')
      setErrorMsg(err?.message || 'Failed to save')
    }
  }

  const handleChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setForm((prev) => ({ ...prev, [field]: value }))
    if (status !== 'idle') setStatus('idle')
  }

  useEffect(() => {
    return () => {
      if (savedResetTimerRef.current) clearTimeout(savedResetTimerRef.current)
    }
  }, [])

  return (
    <div className="max-w-4xl space-y-6">
      {/* Profile Information */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Profile Information</h2>
          <SaveIndicator status={status} errorMsg={errorMsg} />
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">First Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                value={form.first_name}
                onChange={handleChange('first_name')}
                placeholder="First name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">Last Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                value={form.last_name}
                onChange={handleChange('last_name')}
                placeholder="Last name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">Email</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                value={form.email}
                onChange={handleChange('email')}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">Username</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                value={form.username}
                onChange={handleChange('username')}
                placeholder="username"
                autoComplete="username"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">Phone</label>
            <input
              type="tel"
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              value={form.phone}
              onChange={handleChange('phone')}
              placeholder="+1 (555) 123-4567"
            />
          </div>
          <div className="pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || status === 'saving'}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
            >
              {status === 'saving' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <ChangePasswordSection />

      {/* Appearance */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">Appearance</h2>
        <div className="flex items-center justify-between">
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-1">Theme</label>
            <p className="text-xs text-muted-foreground">Toggle between light and dark mode</p>
          </div>
          <ThemeToggle />
        </div>
      </div>

      {/* Role & Organization */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">Role & Organization</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">Role</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              value={user.role_display}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">Office</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              value={user.office?.name || 'No office assigned'}
              disabled
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4 italic">
          Role and office assignments are managed by administrators
        </p>
      </div>
    </div>
  )
}

function SaveIndicator({ status, errorMsg }: { status: SaveStatus; errorMsg: string | null }) {
  if (status === 'saving') {
    return <span className="text-xs text-muted-foreground">Saving…</span>
  }
  if (status === 'saved') {
    return <span className="text-xs text-green-600">Saved</span>
  }
  if (status === 'error') {
    return <span className="text-xs text-destructive">{errorMsg || 'Save failed'}</span>
  }
  return null
}

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const canSubmit = currentPassword && newPassword && confirmPassword && !submitting

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: "New passwords don't match" })
      return
    }
    setSubmitting(true)
    try {
      await apiClient.post('/api/users/me/change-password/', {
        current_password: currentPassword,
        new_password: newPassword,
      })
      setMessage({ type: 'success', text: 'Password updated successfully' })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Failed to update password' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-xl font-semibold text-foreground mb-4">Change Password</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">Current Password</label>
          <input
            type="password"
            className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">New Password</label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">Confirm New Password</label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
        </div>
        {message && (
          <p className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-destructive'}`}>
            {message.text}
          </p>
        )}
        <div>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
          >
            {submitting ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default UserSettings
